import { useState, useEffect } from 'react';
import { useUIStore, useDataStore } from '../stores';
import './CreateProjectModal.css';

function EditProjectModal() {
  const isOpen = useUIStore(state => state.modals.editProject);
  const closeModal = useUIStore(state => state.closeModal);
  const updateProject = useDataStore(state => state.updateProject);
  const showSuccessNotification = useUIStore(state => state.showSuccessNotification);
  const showErrorNotification = useUIStore(state => state.showErrorNotification);

  const [projectToEdit, setProjectToEdit] = useState(null);
  const [formData, setFormData] = useState({
    project_title: '',
    client_name: '',
    address: '',
    start_date: '',
    expected_completion_date: '',
    budget: '',
    progress: 0,
    status: 'ongoing',
    completion_date: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Load project data when modal opens
  useEffect(() => {
    if (isOpen && useUIStore.getState().selectedProjectId) {
      const projects = useDataStore.getState().projects;
      const project = projects.find(p => p.id === useUIStore.getState().selectedProjectId);
      if (project) {
        setProjectToEdit(project);
        // Format dates for input fields (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        setFormData({
          project_title: project.project_title || '',
          client_name: project.client_name || '',
          address: project.address || '',
          start_date: formatDateForInput(project.start_date),
          expected_completion_date: formatDateForInput(project.expected_completion_date),
          budget: project.budget?.toString() || '',
          progress: project.progress || 0,
          status: project.status || 'ongoing',
          completion_date: formatDateForInput(project.completion_date)
        });
        setErrors({});
      }
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_title.trim()) {
      newErrors.project_title = 'Project title is required';
    } else if (formData.project_title.trim().length < 3) {
      newErrors.project_title = 'Project title must be at least 3 characters';
    }

    if (!formData.client_name.trim()) {
      newErrors.client_name = 'Client name is required';
    } else if (formData.client_name.trim().length < 2) {
      newErrors.client_name = 'Client name must be at least 2 characters';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.trim().length < 5) {
      newErrors.address = 'Address must be at least 5 characters';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    } else {
      const startDate = new Date(formData.start_date);
      if (isNaN(startDate.getTime())) {
        newErrors.start_date = 'Invalid start date';
      }
    }

    if (!formData.expected_completion_date) {
      newErrors.expected_completion_date = 'Expected completion date is required';
    } else {
      const completionDate = new Date(formData.expected_completion_date);
      const startDate = new Date(formData.start_date);
      if (isNaN(completionDate.getTime())) {
        newErrors.expected_completion_date = 'Invalid completion date';
      } else if (formData.start_date && completionDate < startDate) {
        newErrors.expected_completion_date = 'Completion date must be after start date';
      }
    }

    // Budget validation - only if project is not completed
    const isCompleted = formData.status === 'completed' || projectToEdit?.status === 'completed';
    if (!isCompleted) {
      if (!formData.budget || formData.budget === '') {
        newErrors.budget = 'Budget is required';
      } else {
        const budget = parseFloat(formData.budget);
        if (isNaN(budget) || budget < 0) {
          newErrors.budget = 'Budget must be a valid positive number';
        }
      }
    }

    if (formData.progress !== '' && (formData.progress < 0 || formData.progress > 100)) {
      newErrors.progress = 'Progress must be between 0 and 100';
    }

    // Completion date validation (optional, but if provided should be after start date)
    if (formData.completion_date) {
      const completionDate = new Date(formData.completion_date);
      const startDate = new Date(formData.start_date);
      if (isNaN(completionDate.getTime())) {
        newErrors.completion_date = 'Invalid completion date';
      } else if (formData.start_date && completionDate < startDate) {
        newErrors.completion_date = 'Completion date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API
      const projectData = {
        project_title: formData.project_title.trim(),
        client_name: formData.client_name.trim(),
        address: formData.address.trim(),
        start_date: new Date(formData.start_date).toISOString(),
        expected_completion_date: new Date(formData.expected_completion_date).toISOString(),
        budget: parseFloat(formData.budget),
        progress: parseInt(formData.progress) || 0,
        status: formData.status
      };

      // Add completion_date if provided
      if (formData.completion_date) {
        projectData.completion_date = new Date(formData.completion_date).toISOString();
      }

      // If project status is completed, don't send budget (restriction)
      // Check both original status and new status to be safe
      const isCompleted = formData.status === 'completed' || projectToEdit?.status === 'completed';
      if (isCompleted) {
        delete projectData.budget;
      }
      
      console.log('[EditProjectModal] Submitting project data:', projectData);
      const result = await updateProject(projectToEdit.id, projectData);
      console.log('[EditProjectModal] Project updated successfully:', result);
      showSuccessNotification('Project updated successfully');
      closeModal('editProject');
      useUIStore.getState().setSelectedProjectId(null);
      setProjectToEdit(null);
      
    } catch (error) {
      console.error('[EditProjectModal] Error updating project:', error);
      
      // Show specific error message if available
      let errorMessage = 'Failed to update project';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.data && typeof error.data === 'object') {
        // Handle validation errors
        const validationErrors = Object.entries(error.data)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        errorMessage = `Validation errors: ${validationErrors}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      closeModal('editProject');
      useUIStore.getState().setSelectedProjectId(null);
      setProjectToEdit(null);
      setErrors({});
    }
  };

  // Get today's date in YYYY-MM-DD format for date inputs
  const today = new Date().toISOString().split('T')[0];
  const isCompleted = formData.status === 'completed' || projectToEdit?.status === 'completed';

  if (!isOpen || !projectToEdit) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Project</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-group">
            <label htmlFor="project_title">Project Title *</label>
            <input
              type="text"
              id="project_title"
              name="project_title"
              value={formData.project_title}
              onChange={handleChange}
              className={errors.project_title ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="Enter project title"
            />
            {errors.project_title && <span className="error-text">{errors.project_title}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="client_name">Client Name *</label>
              <input
                type="text"
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                className={errors.client_name ? 'error' : ''}
                disabled={isSubmitting}
                placeholder="Enter client name"
              />
              {errors.client_name && <span className="error-text">{errors.client_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Address *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className={errors.address ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="Enter project address"
            />
            {errors.address && <span className="error-text">{errors.address}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_date">Start Date *</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className={errors.start_date ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.start_date && <span className="error-text">{errors.start_date}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="expected_completion_date">Expected Completion Date *</label>
              <input
                type="date"
                id="expected_completion_date"
                name="expected_completion_date"
                value={formData.expected_completion_date}
                onChange={handleChange}
                className={errors.expected_completion_date ? 'error' : ''}
                disabled={isSubmitting}
                min={formData.start_date || today}
              />
              {errors.expected_completion_date && <span className="error-text">{errors.expected_completion_date}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="budget">
                Budget (₹) {isCompleted ? '(Cannot edit for completed projects)' : '*'}
              </label>
              <input
                type="number"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className={errors.budget ? 'error' : ''}
                disabled={isSubmitting || isCompleted}
                placeholder="Enter budget"
                min="0"
                step="0.01"
                title={isCompleted ? 'Budget cannot be edited for completed projects' : ''}
              />
              {errors.budget && <span className="error-text">{errors.budget}</span>}
              {isCompleted && (
                <span className="info-text" style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
                  Budget editing is restricted for completed projects
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="progress">Progress (%)</label>
              <input
                type="number"
                id="progress"
                name="progress"
                value={formData.progress}
                onChange={handleChange}
                className={errors.progress ? 'error' : ''}
                disabled={isSubmitting}
                placeholder="0"
                min="0"
                max="100"
                step="1"
              />
              {errors.progress && <span className="error-text">{errors.progress}</span>}
            </div>
          </div>

          {formData.status === 'completed' && (
            <div className="form-group">
              <label htmlFor="completion_date">Completion Date</label>
              <input
                type="date"
                id="completion_date"
                name="completion_date"
                value={formData.completion_date}
                onChange={handleChange}
                className={errors.completion_date ? 'error' : ''}
                disabled={isSubmitting}
                min={formData.start_date || today}
              />
              {errors.completion_date && <span className="error-text">{errors.completion_date}</span>}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              onClick={handleClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProjectModal;

